import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, RiskBar, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * Deterministic retraining and continuous learning (part 86).
 *
 * The page tells one story: which confirmed labels were used, what the model scored before, what
 * the candidate scored on the untouched holdout, and why the gate did or did not promote it. A
 * refused job is shown with the same weight as a promoted one — a refusal is evidence that the
 * pipeline protected the serving model, not an error to hide.
 *
 * The baseline is the last promoted version, so "ก่อนฝึก" is the model that is actually serving,
 * not an arbitrary earlier attempt.
 */

type JobStatus = 'scheduled' | 'training' | 'promoted' | 'refused' | 'failed'

interface RetrainResult {
  id: string
  job_id: string
  model_code: string
  baseline_version: number
  candidate_version: number
  promoted_version: number
  auc_before_bps: number
  auc_after_bps: number
  gain_bps: number
  min_gain_bps: number
  min_auc_bps: number
  decision: 'promoted' | 'refused'
  refusal_code?: string
  train_count: number
  test_count: number
  positive_count: number
  negative_count: number
  iterations: number
  baseline_bias: number
  baseline_weights: number[]
  bias: number
  weights: number[]
  evaluated_at: string
}

interface RetrainJob {
  id: string
  model_code: string
  status: JobStatus
  note?: string
  train_fraction_bps: number
  iterations: number
  learning_rate: number
  min_gain_bps: number
  min_auc_bps: number
  max_samples: number
  feature_keys: string[]
  sample_count: number
  train_count: number
  test_count: number
  scheduled_by?: string
  scheduled_at: string
  started_at?: string
  finished_at?: string
  error_code?: string
  error_detail?: string
  result?: RetrainResult
}

const JOBS_QUERY = ['retrain-jobs'] as const
const resultQuery = (id: string) => ['retrain-result', id] as const

const statusLabel: Record<JobStatus, string> = {
  scheduled: 'รอฝึก',
  training: 'กำลังฝึก',
  promoted: 'เปิดใช้เวอร์ชันใหม่',
  refused: 'ไม่อนุมัติ',
  failed: 'ล้มเหลว',
}

const statusTone: Record<JobStatus, string> = {
  scheduled: 'dim',
  training: 'sev-medium',
  promoted: 'sev-low',
  refused: 'sev-high',
  failed: 'sev-critical',
}

const refusalLabel: Record<string, string> = {
  regressed: 'ตัวชี้วัดลดลงจากรุ่นที่ใช้งาน',
  no_gain: 'พัฒนาการไม่ถึงเกณฑ์ขั้นต่ำ',
  below_floor: 'ยังต่ำกว่าเพดานคุณภาพ',
}

const featureLabel: Record<string, string> = {
  severity: 'ความรุนแรงของข้อค้นพบ',
  risk_score: 'คะแนนความเสี่ยงจากกฎ',
  confidence: 'ความเชื่อมั่นของหลักฐาน',
  amount: 'มูลค่าที่เสี่ยง',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function metricPct(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`
}

function gainText(bps: number): string {
  const sign = bps > 0 ? '+' : ''
  return `${sign}${(bps / 100).toFixed(2)} จุด`
}

function isFinished(status: JobStatus): boolean {
  return status === 'promoted' || status === 'refused' || status === 'failed'
}

function decisionText(result: RetrainResult): string {
  if (result.decision === 'promoted') {
    return `ผ่านเกต: AUC ของชุดทดสอบเพิ่มจาก ${metricPct(result.auc_before_bps)} เป็น ${metricPct(result.auc_after_bps)} ` +
      `ถึงเกณฑ์ขั้นต่ำ ${metricPct(result.min_auc_bps)} และมากกว่าเพดานคุณภาพ ระบบจึงเปิดใช้เวอร์ชัน ${result.promoted_version}`
  }
  const reason = refusalLabel[result.refusal_code ?? ''] ?? 'ไม่ผ่านเกณฑ์'
  switch (result.refusal_code) {
    case 'regressed':
      return `ปฏิเสธ: ${reason} (${metricPct(result.auc_before_bps)} เป็น ${metricPct(result.auc_after_bps)}) ` +
        'ระบบคงรุ่นที่ใช้งานอยู่และไม่บันทึกเวอร์ชันใหม่'
    case 'no_gain':
      return `ปฏิเสธ: ${reason} ต้องเพิ่มอย่างน้อย ${metricPct(result.min_gain_bps)} ` +
        `แต่ได้ ${gainText(result.gain_bps)} ระบบคงรุ่นที่ใช้งานอยู่`
    case 'below_floor':
      return `ปฏิเสธ: ${reason} แม้ดีขึ้นแต่ AUC ยังต่ำกว่า ${metricPct(result.min_auc_bps)} ระบบคงรุ่นที่ใช้งานอยู่`
    default:
      return `ปฏิเสธ: ${reason} ระบบคงรุ่นที่ใช้งานอยู่`
  }
}

export function Retraining() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const canSchedule = ['owner', 'admin', 'audit_manager'].includes(me?.role ?? '')
  const canRun = ['owner', 'admin'].includes(me?.role ?? '')

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [modelCode, setModelCode] = useState('fraud_risk')
  const [note, setNote] = useState('')
  const [trainPct, setTrainPct] = useState(70)
  const [iterations, setIterations] = useState(600)
  const [learningRate, setLearningRate] = useState(0.5)
  const [minGainPct, setMinGainPct] = useState(2)
  const [minAucPct, setMinAucPct] = useState(60)
  const [maxSamples, setMaxSamples] = useState(2000)

  const jobs = useQuery({
    queryKey: JOBS_QUERY,
    queryFn: () => api<RetrainJob[]>('/v1/retraining/jobs?limit=50'),
    staleTime: 5_000,
  })

  const rows = jobs.data ?? []

  useEffect(() => {
    if (!selectedId && rows.length > 0) setSelectedId(rows[0].id)
  }, [rows, selectedId])

  const selected = useMemo(
    () => rows.find((job) => job.id === selectedId),
    [rows, selectedId],
  )

  const detailResult = useQuery({
    queryKey: resultQuery(selectedId ?? 'none'),
    // The endpoint returns { result: {...} }; reading it as a bare result left every field
    // undefined and the page crashed on result.bias.toFixed.
    queryFn: () =>
      api<{ result: RetrainResult }>(`/v1/retraining/jobs/${selectedId}/results`).then((r) => r.result),
    enabled: selectedId !== null && !!selected && isFinished(selected.status) && selected.status !== 'failed',
    retry: false,
  })

  const result = detailResult.data ?? selected?.result

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: JOBS_QUERY })
    if (selectedId) void qc.invalidateQueries({ queryKey: resultQuery(selectedId) })
  }

  const schedule = useMutation({
    mutationFn: () => apiWrite<{ job: RetrainJob }>('/v1/retraining/jobs', 'POST', {
      model_code: modelCode,
      note,
      train_fraction_bps: Math.round(trainPct * 100),
      iterations,
      learning_rate: learningRate,
      min_gain_bps: Math.round(minGainPct * 100),
      min_auc_bps: Math.round(minAucPct * 100),
      max_samples: maxSamples,
    }),
    onSuccess: (body) => {
      setNotice('สร้างงานฝึกแล้ว ยังไม่อ่านข้อมูลจนกว่าจะสั่งฝึก')
      setSelectedId(body.job.id)
      setNote('')
      refresh()
    },
  })

  const run = useMutation({
    mutationFn: (jobID: string) => apiWrite<{ job: RetrainJob }>(`/v1/retraining/jobs/${jobID}/run`, 'POST', {}),
    onSuccess: (body) => {
      const job = body.job
      if (job.status === 'promoted') {
        setNotice(`ฝึกเสร็จและเปิดใช้เวอร์ชัน ${job.result?.promoted_version ?? ''} แล้ว`)
      } else if (job.status === 'refused') {
        setNotice('ฝึกเสร็จแต่ถูกปฏิเสธ ระบบคงรุ่นที่ใช้งานอยู่')
      } else {
        setNotice(job.error_detail || 'ฝึกไม่สำเร็จ ตรวจสอบรายละเอียดในงาน')
      }
      setSelectedId(job.id)
      refresh()
    },
  })

  const counts = useMemo(() => {
    const finished = rows.filter((job) => job.result)
    const promoted = finished.filter((job) => job.status === 'promoted')
    const refused = finished.filter((job) => job.status === 'refused')
    const bestGain = finished.reduce((best, job) => Math.max(best, job.result?.gain_bps ?? 0), 0)
    return {
      total: rows.length,
      promoted: promoted.length,
      refused: refused.length,
      bestGain,
    }
  }, [rows])

  const mutationError = schedule.error ?? run.error

  return (
    <div className="page">
      <PageHead
        title="การฝึกโมเดลซ้ำ"
        sub="เลือกป้ายกำกับที่ยืนยันแล้ว ฝึกแบบกำหนดจำนวนรอบตายตัว วัดผลบนชุดทดสอบตามลำดับเวลา และเปิดใช้เฉพาะรุ่นที่ตัวชี้วัดดีขึ้นจริง"
        actions={<span className="chip gold">เปิดใช้เฉพาะรุ่นที่ผ่านเกต</span>}
      />

      {mutationError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {errorText(mutationError, 'ดำเนินการไม่สำเร็จ')}
        </div>
      ) : null}
      {notice ? (
        <div className="card card-pad rise" style={{ marginBottom: 16, borderColor: 'rgba(200,169,106,0.35)' }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12.5 }}>{notice}</span>
            <button className="btn btn-sm btn-ghost" onClick={() => setNotice(null)}>ปิดข้อความ</button>
          </div>
        </div>
      ) : null}

      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat label="งานฝึกทั้งหมด" value={counts.total} foot="นับทุกสถานะ รวมงานที่ยังไม่สั่งฝึก" />
        <Stat label="เปิดใช้เวอร์ชันใหม่" value={counts.promoted} foot="เฉพาะรุ่นที่ผ่านเกตและบันทึกผลแล้ว" />
        <Stat label="ถูกปฏิเสธ" value={counts.refused} foot="ระบบคงรุ่นเดิมเมื่อตัวชี้วัดไม่ผ่าน" />
        <Stat label="พัฒนาการสูงสุด" value={counts.bestGain > 0 ? metricPct(counts.bestGain) : '—'}
          foot="ส่วนต่าง AUC บนชุดทดสอบของงานที่ประเมินแล้ว" />
      </div>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span className="card-label" style={{ margin: 0 }}>สร้างงานฝึกใหม่</span>
          <span className="dim" style={{ fontSize: 10.5 }}>ค่าที่บันทึกจะถูกตรึงไว้กับงานนี้</span>
        </div>
        <div className="grid-3" style={{ marginTop: 12 }}>
          <div className="field">
            <label htmlFor="retrain-model">รหัสโมเดล</label>
            <input id="retrain-model" className="input" value={modelCode} placeholder="fraud_risk"
              onChange={(e) => setModelCode(e.target.value)} />
            <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>ต้องมีอยู่ในโมเดลความเสี่ยงขององค์กร</div>
          </div>
          <div className="field">
            <label htmlFor="retrain-split">สัดส่วนข้อมูลฝึก (%)</label>
            <input id="retrain-split" className="input" type="number" min={50} max={90} value={trainPct}
              onChange={(e) => setTrainPct(Number(e.target.value))} />
            <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>ส่วนที่เหลือคือชุดทดสอบที่ใหม่กว่าเสมอ</div>
          </div>
          <div className="field">
            <label htmlFor="retrain-iterations">จำนวนรอบฝึก</label>
            <input id="retrain-iterations" className="input" type="number" min={1} max={5000} value={iterations}
              onChange={(e) => setIterations(Number(e.target.value))} />
          </div>
        </div>
        <div className="grid-3" style={{ marginTop: 10 }}>
          <div className="field">
            <label htmlFor="retrain-lr">อัตราการเรียนรู้</label>
            <input id="retrain-lr" className="input" type="number" step="0.01" min={0.001} max={5} value={learningRate}
              onChange={(e) => setLearningRate(Number(e.target.value))} />
          </div>
          <div className="field">
            <label htmlFor="retrain-gain">พัฒนาการขั้นต่ำ (จุดเปอร์เซ็นต์)</label>
            <input id="retrain-gain" className="input" type="number" min={0} max={50} value={minGainPct}
              onChange={(e) => setMinGainPct(Number(e.target.value))} />
          </div>
          <div className="field">
            <label htmlFor="retrain-floor">AUC ขั้นต่ำ (%)</label>
            <input id="retrain-floor" className="input" type="number" min={50} max={100} value={minAucPct}
              onChange={(e) => setMinAucPct(Number(e.target.value))} />
          </div>
        </div>
        <div className="grid-2" style={{ marginTop: 10 }}>
          <div className="field">
            <label htmlFor="retrain-max">จำนวนป้ายกำกับสูงสุด</label>
            <input id="retrain-max" className="input" type="number" min={16} max={10000} value={maxSamples}
              onChange={(e) => setMaxSamples(Number(e.target.value))} />
            <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>อ่านป้ายล่าสุดก่อน แล้วจึงเรียงตามเวลาเพื่อแบ่งชุด</div>
          </div>
          <div className="field">
            <label htmlFor="retrain-note">บันทึกเหตุผล</label>
            <input id="retrain-note" className="input" maxLength={1000} value={note}
              placeholder="ฝึกประจำรอบจากผลยืนยันล่าสุด" onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <div className="row" style={{ marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" disabled={!canSchedule || !modelCode || schedule.isPending}
            onClick={() => schedule.mutate()}>
            {schedule.isPending ? 'กำลังสร้างงาน…' : 'สร้างงานฝึก'}
          </button>
          {!canSchedule ? (
            <span className="dim" style={{ fontSize: 11.5, alignSelf: 'center' }}>
              การสร้างงานฝึกทำได้โดยเจ้าขององค์กร ผู้ดูแลระบบ หรือผู้จัดการตรวจสอบ
            </span>
          ) : null}
        </div>
      </div>

      <div className="grid-main">
        <div>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="card-label" style={{ margin: 0 }}>ประวัติการฝึก</span>
            <span className="chip" style={{ marginLeft: 'auto' }}>{rows.length} งาน</span>
          </div>
          {jobs.isLoading ? <Skel h={280} /> : rows.length === 0 ? (
            <Empty title="ยังไม่มีงานฝึก" body="สร้างงานแรกเพื่อทดลองฝึกจากป้ายกำกับที่ยืนยันแล้ว ระบบจะไม่เปิดใช้รุ่นที่ไม่ดีขึ้น" />
          ) : (
            <div className="table-wrap">
              <table className="table data">
                <thead>
                  <tr>
                    <th>โมเดล</th>
                    <th>สถานะ</th>
                    <th>ก่อน / หลัง</th>
                    <th>เวอร์ชัน</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((job) => (
                    <tr key={job.id} onClick={() => setSelectedId(job.id)}
                      style={{ cursor: 'pointer', background: job.id === selectedId ? 'rgba(200,169,106,0.06)' : undefined }}>
                      <td>
                        <div className="mono" style={{ fontSize: 11.5 }}>{job.model_code}</div>
                        <div className="dim" style={{ fontSize: 10.5 }}>{thaiDateTime(job.scheduled_at)}</div>
                      </td>
                      <td>
                        <span className={`chip ${statusTone[job.status]}`}>{statusLabel[job.status]}</span>
                      </td>
                      <td className="tnum" style={{ fontSize: 11.5 }}>
                        {job.result
                          ? `${metricPct(job.result.auc_before_bps)} → ${metricPct(job.result.auc_after_bps)}`
                          : <span className="dim">ยังไม่ประเมิน</span>}
                      </td>
                      <td className="tnum">
                        {job.result?.promoted_version
                          ? <span className="chip gold">v{job.result.promoted_version}</span>
                          : <span className="dim">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          {!selected ? (
            <div className="card card-pad"><span className="dim" style={{ fontSize: 12 }}>เลือกงานเพื่อดูรายละเอียด</span></div>
          ) : (
            <>
              <div className="card card-pad" style={{ marginBottom: 16 }}>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="heading-m mono" style={{ fontSize: 15 }}>{selected.model_code}</div>
                    <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>
                      สร้างเมื่อ {thaiDateTime(selected.scheduled_at)}
                    </div>
                  </div>
                  <span className={`chip ${statusTone[selected.status]}`}>{statusLabel[selected.status]}</span>
                </div>

                {selected.note ? (
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.7 }}>{selected.note}</div>
                ) : null}

                <div className="grid-2" style={{ marginTop: 12 }}>
                  <div>
                    <div className="card-label">พารามิเตอร์ที่ตรึงไว้</div>
                    <div className="dim" style={{ fontSize: 11, lineHeight: 1.9, marginTop: 4 }}>
                      แบ่งฝึก {selected.train_fraction_bps / 100}% · {selected.iterations} รอบ<br />
                      อัตราการเรียนรู้ {selected.learning_rate} · สูงสุด {selected.max_samples} ป้าย<br />
                      ต้องการพัฒนาการ {metricPct(selected.min_gain_bps)} · AUC ขั้นต่ำ {metricPct(selected.min_auc_bps)}
                    </div>
                  </div>
                  <div>
                    <div className="card-label">ข้อมูลที่ใช้</div>
                    <div className="dim" style={{ fontSize: 11, lineHeight: 1.9, marginTop: 4 }}>
                      ทั้งหมด {selected.sample_count} รายการ<br />
                      ฝึก {selected.train_count} · ทดสอบ {selected.test_count}<br />
                      {result ? `ผลบวกในชุดทดสอบ ${result.positive_count} · ผลลบ ${result.negative_count}` : 'ยังไม่ประเมิน'}
                    </div>
                  </div>
                </div>

                {selected.status === 'scheduled' ? (
                  <div className="row" style={{ marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" disabled={!canRun || run.isPending}
                      onClick={() => run.mutate(selected.id)}>
                      {run.isPending ? 'กำลังฝึก…' : 'สั่งฝึกและประเมินผล'}
                    </button>
                    <span className="dim" style={{ fontSize: 10.5, alignSelf: 'center' }}>
                      การฝึกจะเปิดใช้รุ่นใหม่ทันทีเมื่อผ่านเกต
                    </span>
                  </div>
                ) : null}
                {!canRun ? (
                  <div className="dim" style={{ fontSize: 10.5, marginTop: 8 }}>
                    การสั่งฝึกทำได้โดยเจ้าขององค์กรหรือผู้ดูแลระบบเท่านั้น
                  </div>
                ) : null}

                {selected.status === 'failed' ? (
                  <div className="form-error" style={{ marginTop: 12 }}>
                    {selected.error_code}: {selected.error_detail || 'ฝึกไม่สำเร็จ'}
                  </div>
                ) : null}
              </div>

              {selected.status === 'training' ? (
                <div className="card card-pad"><span className="dim" style={{ fontSize: 12 }}>งานนี้กำลังฝึกอยู่ ผลจะปรากฏเมื่อเสร็จ</span></div>
              ) : result ? (
                <div className="card card-pad">
                  <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
                    <span className="card-label" style={{ margin: 0 }}>ผลประเมินบนชุดทดสอบ</span>
                    <span className={`chip ${result.decision === 'promoted' ? 'sev-low' : 'sev-high'}`}>
                      {result.decision === 'promoted' ? 'ผ่านเกต' : 'ปฏิเสธ'}
                    </span>
                  </div>

                  <div className="grid-2">
                    <div>
                      <div className="dim" style={{ fontSize: 11 }}>ก่อนฝึก · รุ่น {result.baseline_version === 0 ? 'เริ่มต้น' : `v${result.baseline_version}`}</div>
                      <div className="heading-l tnum" style={{ marginTop: 2 }}>{metricPct(result.auc_before_bps)}</div>
                      <RiskBar score={result.auc_before_bps / 100} />
                    </div>
                    <div>
                      <div className="dim" style={{ fontSize: 11 }}>หลังฝึก · ผู้สมัคร v{result.candidate_version}</div>
                      <div className="heading-l tnum" style={{ marginTop: 2, color: result.gain_bps > 0 ? 'var(--color-gold)' : undefined }}>
                        {metricPct(result.auc_after_bps)}
                      </div>
                      <RiskBar score={result.auc_after_bps / 100} />
                    </div>
                  </div>

                  <div className="row" style={{ marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
                    <span className={`chip ${result.gain_bps > 0 ? 'gold' : 'dim'}`}>ส่วนต่าง {gainText(result.gain_bps)}</span>
                    <span className="chip">ฝึก {result.train_count} · ทดสอบ {result.test_count}</span>
                    <span className="chip">{result.iterations} รอบ</span>
                    {result.promoted_version > 0 ? (
                      <span className="chip gold">เปิดใช้ v{result.promoted_version}</span>
                    ) : (
                      <span className="chip dim">ไม่เปลี่ยนเวอร์ชัน</span>
                    )}
                  </div>

                  <div className="dim" style={{ fontSize: 11.5, marginTop: 12, lineHeight: 1.8 }}>
                    {decisionText(result)}
                  </div>

                  <div className="row" style={{ justifyContent: 'space-between', marginTop: 14, marginBottom: 6 }}>
                    <span className="card-label" style={{ margin: 0 }}>น้ำหนักหลังฝึก</span>
                    <span className="dim" style={{ fontSize: 10.5 }}>ไบแอส {result.bias.toFixed(4)}</span>
                  </div>
                  <div className="table-wrap">
                    <table className="table data">
                      <thead>
                        <tr>
                          <th>คุณลักษณะ</th>
                          <th>ก่อน</th>
                          <th>หลัง</th>
                          <th>เปลี่ยน</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.feature_keys.map((key, index) => {
                          const before = result.baseline_weights[index] ?? 0
                          const after = result.weights[index] ?? 0
                          return (
                            <tr key={key}>
                              <td>{featureLabel[key] ?? key}<div className="mono dim" style={{ fontSize: 10 }}>{key}</div></td>
                              <td className="tnum">{before.toFixed(4)}</td>
                              <td className="tnum">{after.toFixed(4)}</td>
                              <td className="tnum" style={{ color: after - before > 0 ? 'var(--color-gold)' : undefined }}>
                                {after - before >= 0 ? '+' : ''}{(after - before).toFixed(4)}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="dim" style={{ fontSize: 10.5, marginTop: 8 }}>
                    ประเมินเมื่อ {thaiDateTime(result.evaluated_at)} · ผลนี้ถูกบันทึกแบบเพิ่มอย่างเดียว แก้ไขย้อนหลังไม่ได้
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
